// 🎨📏 **글자 토막 — 편집 축**(회장님 2026-09-15 «텍스트 박스 안에서 글자 별로 색상이나 폰트 크기»)
// ─────────────────────────────────────────────────────────────────────────────
// 유저가 입력칸에서 **일부를 선택**해 색·크기를 바꾼 것을 «원문(`slotText`) 위의 구간»으로 들고 있는다.
// 렌더로 나가는 토막(`lib/text-runs`)은 줄바꿈이 박힌 뒤의 **잘라 놓은 글자**라 여기서 쓸 수 없다 —
// 줄바꿈이 끼면 위치가 밀려 «몇 번째 글자»가 달라지기 때문이다. ⇒ 편집 축은 원문 기준 구간을 들고,
// 렌더 축은 토막을 받는다. 변환은 `buildSlotOverlay` 한 곳뿐이다(`text` ↔ `slotText` 와 같은 관용구).
//
// ★인덱스 단위는 **UTF-16 코드유닛**이다★ — 입력칸의 `selectionStart`/`selectionEnd` 가 그 단위라,
//   다른 단위를 쓰면 이모지가 섞인 순간 선택 범위가 한 칸씩 어긋난다.
//
// ★자막 강조(`variety-caption-plan.EmphasisRun`)와 같은 모양, 다른 자유도★ — 그쪽은 `tone` 2종
//   (key·alert) 고정이고 여기는 **색이 자유값 + 크기 배수**다. 그래서 겹침 처분을 «거부»가 아니라
//   «덮어쓰기»로 둔다: 유저가 빨강 위에 큰 글씨를 겹쳐 고르는 것은 지극히 정상적인 조작이다.
import { clampRunScale, type MbTextRun } from "../text-runs";

/** 🎨 **원문 위의 한 구간** — `[start, end)` (UTF-16 코드유닛 · 끝은 미포함). */
export interface SlotTextRun {
  start: number;
  end: number;
  /** 이 구간만의 색. 없으면 상자 기본색. */
  color?: string;
  /** 이 구간만의 크기 배수(1 = 기본). 없으면 기본 크기. */
  scale?: number;
}

/** 🎨 한 글자에 걸린 꾸밈(내부 표현) — 구간을 «글자별»로 펴 두면 겹침이 구조적으로 사라진다. */
export type SlotCharStyle = { color?: string; scale?: number };

/** 🎛 선택 구간에 걸 변경 — 필드가 **있으면서 null** 이면 «그 축을 지운다». */
export type SlotRunPatch = { color?: string | null; scale?: number | null };

const isPlain = (s: SlotCharStyle) => s.color === undefined && s.scale === undefined;
const same = (a: SlotCharStyle, b: SlotCharStyle) => a.color === b.color && a.scale === b.scale;

/**
 * 🎨 **구간 목록 → 글자별 꾸밈**.
 * ★여기서 겹침·역순·범위 밖이 전부 사라진다★ — 뒤에 온 구간이 이긴다(유저의 마지막 조작이 이긴다).
 */
export function styleByChar(runs: readonly SlotTextRun[] | undefined, len: number): SlotCharStyle[] {
  const out: SlotCharStyle[] = Array.from({ length: Math.max(0, len) }, () => ({}));
  for (const r of runs ?? []) {
    const s = Math.max(0, Math.min(out.length, Math.floor(Number(r?.start))));
    const e = Math.max(0, Math.min(out.length, Math.floor(Number(r?.end))));
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;
    const color = typeof r.color === "string" && r.color.trim() ? r.color : undefined;
    const scale = clampRunScale(r.scale);
    for (let i = s; i < e; i++) {
      if (color !== undefined) out[i].color = color;
      if (scale !== undefined) out[i].scale = scale;
    }
  }
  return out;
}

/** 🎨 글자별 꾸밈 → 최소 구간 목록(꾸밈 없는 글자는 구간을 안 만든다). */
export function collapseCharStyles(chars: readonly SlotCharStyle[]): SlotTextRun[] {
  const out: SlotTextRun[] = [];
  let i = 0;
  while (i < chars.length) {
    if (isPlain(chars[i])) { i += 1; continue; }
    let j = i + 1;
    while (j < chars.length && same(chars[i], chars[j])) j += 1;
    out.push({
      start: i,
      end: j,
      ...(chars[i].color ? { color: chars[i].color } : {}),
      ...(chars[i].scale !== undefined ? { scale: chars[i].scale } : {}),
    });
    i = j;
  }
  return out;
}

/** 🎨 저장분 정리 — 겹침을 풀고 범위를 글자 수 안으로 자른다(저장 전·읽은 직후 둘 다 여기를 지난다). */
export function normalizeSlotRuns(runs: readonly SlotTextRun[] | undefined, len: number): SlotTextRun[] {
  return collapseCharStyles(styleByChar(runs, len));
}

/**
 * 🎛 **선택한 구간에 색·크기를 건다**. 구간이 비었으면(`end <= start`) 아무것도 안 한 목록을 돌려준다
 * — 선택 없이 누른 것을 «전체 적용»으로 읽으면 유저가 의도하지 않은 대량 변경이 된다.
 */
export function applySlotRunStyle(
  runs: readonly SlotTextRun[] | undefined,
  len: number,
  start: number,
  end: number,
  patch: SlotRunPatch,
): SlotTextRun[] {
  const chars = styleByChar(runs, len);
  const s = Math.max(0, Math.min(chars.length, Math.floor(start)));
  const e = Math.max(0, Math.min(chars.length, Math.floor(end)));
  if (!(e > s)) return collapseCharStyles(chars);
  for (let i = s; i < e; i++) {
    if ("color" in patch) {
      if (patch.color == null || !String(patch.color).trim()) delete chars[i].color;
      else chars[i].color = String(patch.color);
    }
    if ("scale" in patch) {
      const sc = patch.scale == null ? undefined : clampRunScale(patch.scale);
      if (sc === undefined) delete chars[i].scale;
      else chars[i].scale = sc;
    }
  }
  return collapseCharStyles(chars);
}

/**
 * ✏️ **글자를 고친 뒤 구간을 다시 맞춘다**.
 *
 * ★이게 없으면 앞에 한 글자만 쳐도 색이 통째로 밀린다★ — 유저 눈에는 «색이 제멋대로 움직인다»로
 *   보이고 되돌릴 방법이 없다. 앞뒤 공통부를 재서 **바뀐 토막 한 군데**로 접는다(입력칸의 한 번
 *   편집은 언제나 한 군데다 — 붙여넣기·지우기·조합 모두 포함).
 * 바뀐 토막 **안에만** 있던 구간은 사라진다(그 글자들이 실제로 없어졌다).
 */
export function remapSlotRuns(
  runs: readonly SlotTextRun[] | undefined,
  oldText: string,
  newText: string,
): SlotTextRun[] {
  const o = String(oldText ?? "");
  const n = String(newText ?? "");
  if (o === n) return normalizeSlotRuns(runs, n.length);
  let p = 0;
  while (p < o.length && p < n.length && o[p] === n[p]) p += 1;
  let suf = 0;
  while (suf < o.length - p && suf < n.length - p && o[o.length - 1 - suf] === n[n.length - 1 - suf]) suf += 1;
  const delta = n.length - o.length;
  const tail = o.length - suf;
  const map = (i: number) => (i <= p ? i : i >= tail ? i + delta : p);
  const moved = (runs ?? [])
    .map((r) => ({ ...r, start: map(r.start), end: map(r.end) }))
    .filter((r) => r.end > r.start);
  return normalizeSlotRuns(moved, n.length);
}

/**
 * 🎨 **원문 + 구간 → 글자별 꾸밈이 실린 글자 목록**(줄바꿈 계산의 입력).
 * 코드포인트 단위로 묶되(써로게이트 쌍이 반으로 갈리지 않게) 꾸밈은 **첫 코드유닛**의 것을 쓴다.
 */
export function styledCharsOf(text: string, runs?: readonly SlotTextRun[]): { ch: string; color?: string; scale?: number }[] {
  const src = String(text ?? "");
  const st = styleByChar(runs, src.length);
  const out: { ch: string; color?: string; scale?: number }[] = [];
  let i = 0;
  for (const ch of src) {
    out.push({ ch, ...(st[i] ?? {}) });
    i += ch.length;
  }
  return out;
}

/** 🎨 글자 목록 → 렌더 토막(같은 꾸밈이 이어지면 한 토막으로 붙인다). */
export function runsFromStyledChars(chars: readonly { ch: string; color?: string; scale?: number }[]): MbTextRun[] {
  const out: MbTextRun[] = [];
  for (const c of chars) {
    const last = out[out.length - 1];
    if (last && last.color === c.color && last.scale === c.scale) last.text += c.ch;
    else out.push({ text: c.ch, ...(c.color ? { color: c.color } : {}), ...(c.scale !== undefined ? { scale: c.scale } : {}) });
  }
  return out;
}
